import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #isAnimating = false;

  #updateMaxHeight = () => {
    console.log('updateMaxHeight');
    const detailsContent = this.querySelector('.details-content');
    console.log('detailsContent', detailsContent);
    if (detailsContent) {      
      // Quick measurement - content is already rendered
      const naturalHeight = detailsContent.scrollHeight;
      this.details.style.setProperty('--accordion-height', `${naturalHeight}px`);
    }
  };

  #handleDetailsToggle = (event) => {
    const details = event.target;
    
    if (details.open) {
      // Opening - update height for expand animation
      this.#updateMaxHeight();
    }
  };

  #animateClose = () => {
    if (this.#isAnimating) return;
    
    this.#isAnimating = true;
    
    // Set current height before starting close animation
    this.#updateMaxHeight();
    
    // Force a reflow to ensure the height is set
    this.details.offsetHeight;
    
    // Set height to 0 to trigger CSS transition
    this.details.style.setProperty('--accordion-height', '0px');
    
    // Wait for transition to complete, then actually close the details
    const detailsContent = this.querySelector('.details-content');
    const duration = detailsContent 
      ? parseFloat(getComputedStyle(detailsContent).transitionDuration) * 1000 
      : 300; // fallback to 300ms
    
    setTimeout(() => {
      this.details.open = false;
      this.#isAnimating = false;
    }, duration);
  };

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleDetailsToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Initial height calculation if accordion is open by default
    if (this.details.open) {
      this.#updateMaxHeight();
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // If accordion is open and not animating, intercept close to animate it
    if (this.details.open && !this.#isAnimating) {
      event.preventDefault();
      this.#animateClose();
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      // Use animated close instead of instant close
      if (this.details.open && !this.#isAnimating) {
        this.#animateClose();
      }
      this.summary.focus();
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}